import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import {
  Badge,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  getNotifications,
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  markNotificationAsUnread,
  deleteNotification,
} from '../actions/Notifications';

import NotificationsDropdownItem from './Notifications/DropdownItem';

import { getShare } from '../helpers/Auth';
import { updateNotificationNumberInPageTitle } from '../helpers/Notifications';

import Loading from './Loading';
import NothingFound from './NothingFound';

const NotificationsDropdown = ({
  dispatch,
  location,
  profile,
  notifications,
  notificationsUnread,
  notificationsLoading,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const loadMore = useCallback(() => {
    if (!profile?.uuid) {
      return;
    }

    dispatch(getNotifications([]));
    dispatch(fetchNotifications());
  }, [dispatch, profile.uuid]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    updateNotificationNumberInPageTitle(notificationsUnread);
  }, [notificationsUnread]);

  const handleMarkAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
  };

  const handleMarkAsUnread = (notificationId) => {
    dispatch(markNotificationAsUnread(notificationId));
  };

  const handleDelete = (notificationId) => {
    dispatch(deleteNotification(notificationId));
  };

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Dropdown
      nav
      direction={getShare() ? 'down' : 'right'}
      className={classnames('nav-notifications', { active: isOpen })}
      isOpen={isOpen}
      toggle={toggleIsOpen}
    >
      <DropdownToggle nav>
        <span className="nav-item-icon">
          <i className="far fa-bell" />
        </span>
        <span className="nav-item-title">
          {intl.formatMessage({ defaultMessage: 'Notifications' })}
        </span>
        {notificationsUnread > 0 && (
          <Badge color="primary" pill>
            {notificationsUnread > 99 ? '99+' : notificationsUnread}
          </Badge>
        )}
      </DropdownToggle>
      <DropdownMenu right className="notifications-dropdown" container="body">
        <DropdownItem header>
          <span>
            {intl.formatMessage({ defaultMessage: 'Notifications' })}
          </span>
          <span className="notifications-settings">
            <Link to="/profile/edit?tab=notifications">
              <i className="far fa-cog" />
              {intl.formatMessage({ defaultMessage: 'Settings' })}
            </Link>
          </span>
        </DropdownItem>
        <DropdownItem divider />
        <div id="notifications-dropdown-items" className="notifications-dropdown-items" role="menuitem">
          {notifications.length > 0 ? (
            <>
              {notifications.map(notification => (
                <NotificationsDropdownItem
                  key={notification.id}
                  notification={notification}
                  handleMarkAsRead={handleMarkAsRead}
                  handleMarkAsUnread={handleMarkAsUnread}
                  handleDelete={handleDelete}
                />
              ))}
            </>
          ) : (
            <>
              {!notificationsLoading && (
                <NothingFound text={intl.formatMessage({ defaultMessage: `You don't have any notifications yet` })} />
              )}
            </>
          )}
          {isOpen && (
            <>
              {notificationsLoading ? (
                <Loading />
              ) : (
                <Waypoint
                  bottomOffset="-20px"
                  scrollableAncestor={document.getElementById('notifications-dropdown-items')}
                  onEnter={() => { loadMore(); }}
                />
              )}
            </>
          )}
        </div>
        {notifications.length > 0 && (
          <DropdownItem header className="dropdown-footer">
            <Button
              color="primary"
              size="sm"
              tag={Link}
              to="/notifications"
            >
              {intl.formatMessage({ defaultMessage: 'See all' })}
            </Button>
            <Button
              color="secondary"
              size="sm"
              disabled={notificationsUnread === 0}
              onClick={handleMarkAllAsRead}
            >
              {intl.formatMessage({ defaultMessage: 'Mark all as read' })}
            </Button>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  notifications: state.Notifications.notifications,
  notificationsUnread: state.Notifications.unread,
  notificationsLoading: state.Notifications.loading,
});

NotificationsDropdown.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  notifications: PropTypes.array.isRequired,
  notificationsUnread: PropTypes.number.isRequired,
  notificationsLoading: PropTypes.bool.isRequired,
};

NotificationsDropdown.defaultProps = {
  profile: null,
};

export default withRouter(connect(mapStateToProps)(NotificationsDropdown));
